import {
    ArrayTemplate,
    downloadWorkbook,
    DynamicField,
    exportExcelFromWorkBook,
    getRecordRowCountForSheet,
    QuantityField,
    rangedExcelFileName,
    RequiredField,
    SheetTemplate,
    WorkBookTemplate,
    writeExcelFromWorkBook
} from "../../../../common/upload/excel.utils";
import {SourcesProvider} from "../../../../utils/source-providers/sources-provider";
import {
    AggregatedDataPoint,
    ConnectionType,
    DataType,
    GetMeasurementsByConnection,
    GetMeasurementsByMeter,
    MeasurementsResult,
    Meter,
    MeterType,
    TimeRange,
    TimeResolution
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {RefdataUtils} from "../../refdata-utils";
import lodash, {cloneDeep} from "lodash";
import {Slot} from "../../../../utils/chart-data-provider";
import {DashboardContext} from "../../../dashboard/dashboard.context";
import {AppContext} from "../../../../app-context";
import {mergeMap, tap} from "rxjs/operators";
import * as XLSX from "xlsx-js-style";
import moment from "moment";
import {removeItem} from "../../../../common/utils";
import {combineLatest, Observable, of} from 'rxjs';
import {meterEnergyType} from '../thermal-meters';
import {effectiveMeterType, isCalculatedResidual, isMeterVisible} from '../../meters/submeter-role.utils';
import {sendQuery} from "../../../../common/app-common-utils";
import {Entity, EntityType} from "../../../../handlers/entity";
import {MeterViewEntity} from "../../../../handlers/meter-views-standalone-handler";
import {Measurements} from "../../../measurements-component/measurements";
import {TranslateDirective} from "../../../../common/utils/translate.directive";

export function downloadBulkMonthData(sourceProvider: SourcesProvider<any>, timeRange: TimeRange,
                                      aggregated: boolean = true) {
    const info = prepareDownload(timeRange, bulkDataDownloadTemplate);
    combineLatest([sendQuery("getConnectionsAsEntities"), sendQuery("getMeterViews"), getMeasurements(timeRange, sourceProvider, !aggregated)])
        .subscribe(([entities, allMeterViews, data]) => {
            const meterViews = allMeterViews.filter(m => m.meterView.meterType != MeterType.INTERMEDIATE)
            const records = {
                data: getConnectionData(timeRange, data, entities, meterViews, sourceProvider, info.slots, info.yearSlots, info.dynamicColumns, aggregated)
            };
            const rowCount = getRecordRowCountForSheet(info.template.sheets[0].template, records);
            exportExcelFromWorkBook(downloadWorkbook("/assets/templates/bulk-data-download-new.xlsx")
                .pipe(tap(wb => {
                    const sheet = wb.Sheets["Data"];
                    const sheetTemplate = info.template.sheets.find(s => s.name === "Data");
                    applyBulkHeaders(sheet);
                    addDynamicColumns(sheet, sheetTemplate, info.dynamicColumns, rowCount);
                })), info.template, records, rangedExcelFileName(TranslateDirective.getTranslation(aggregated
                    ? 'monthly measurement data' : 'monthly measurement data (detailed)', true) as string, timeRange), {
                sheets: {
                    Data: {
                        title: TranslateDirective.getTranslation(aggregated
                            ? 'monthly measurement data' : 'monthly measurement data (detailed)', true) as string
                    }
                }
            });
        });
}

export function downloadSubmeterData(sourceProvider: SourcesProvider<any>, timeRange: TimeRange, meterIds?: string[]) {
    const info = prepareDownload(timeRange, submetersDataDownloadTemplate);
    combineLatest([sendQuery("getMetersAsEntities"), sendQuery("getMeterViews"), getSubmeterMeasurements(timeRange, sourceProvider, meterIds)])
        .subscribe(([entities, allMeterViews, data]) => {
            const {overviewData, chargePointData} = getSubmeterData(
                timeRange, data, entities, allMeterViews, sourceProvider,
                info.slots, info.yearSlots, info.dynamicColumns, meterIds);
            const records = {
                data: overviewData,
                chargePoints: chargePointData
            };
            const submeterColumns = getSubmeterExportColumns(overviewData.concat(chargePointData));
            configureSubmeterTemplate(info.template, submeterColumns);
            const dynamicColumns = getDynamicColumns(info.template, info.slots, info.yearSlots);
            const dataTemplate = info.template.sheets.find(s => s.name === "Data");
            const chargePointTemplate = info.template.sheets.find(s => s.name === "ChargePoints");
            const rowCountData = getRecordRowCountForSheet(dataTemplate.template, {data: overviewData});
            const rowCountChargePoints = getRecordRowCountForSheet(
                chargePointTemplate.template, {chargePoints: chargePointData});
            exportExcelFromWorkBook(downloadWorkbook("/assets/templates/submeters-download-data.xlsx")
                    .pipe(tap(wb => {
                        const dataSheet = wb.Sheets["Data"];
                        applySubmeterHeaders(dataSheet, submeterColumns);
                        addDynamicColumns(dataSheet, dataTemplate, dynamicColumns, rowCountData);
                        const chargePointSheet = wb.Sheets["ChargePoints"];
                        applySubmeterHeaders(chargePointSheet, submeterColumns);
                        addDynamicColumns(chargePointSheet, chargePointTemplate,
                            dynamicColumns, rowCountChargePoints);
                    })),
                info.template, records, rangedExcelFileName(
                    TranslateDirective.getTranslation('monthly submeter data', true) as string, timeRange), {
                    sheets: {
                        Data: {
                            title: TranslateDirective.getTranslation('monthly submeter data', true) as string
                        },
                        ChargePoints: {
                            title: TranslateDirective.getTranslation('charge point data', true) as string
                        }
                    }
                }
            );
        });
}

export function writeBulkMonthDataAsExcel(sourceProvider: SourcesProvider<any>, timeRange: TimeRange): Observable<any> {
    const info = prepareDownload(timeRange, bulkDataDownloadTemplate);
    return combineLatest([sendQuery("getConnectionsAsEntities"), sendQuery("getMeterViews"), getMeasurements(timeRange, sourceProvider, false)])
        .pipe(mergeMap(([connections, allMeterViews, data]) => {
            const meterViews = allMeterViews.filter(m => m.meterView.meterType != MeterType.INTERMEDIATE)
            const records = {
                data: getConnectionData(timeRange, data, connections, meterViews, sourceProvider,
                    info.slots, info.yearSlots, info.dynamicColumns, false)
            };
            const rowCount = getRecordRowCountForSheet(info.template.sheets[0].template, records);
            return writeExcelFromWorkBook(downloadWorkbook("/assets/templates/bulk-data-download.xlsx")
                    .pipe(tap(wb => addDynamicColumns(wb.Sheets["Data"], info.template.sheets.find(
                        s => s.name === "Data"), info.dynamicColumns, rowCount))),
                info.template, records, {
                    bookType: "xlsx",
                    type: "base64"
                });
        }));
}

function prepareDownload(timeRange: TimeRange, temp: WorkBookTemplate): BulkDownloadInfo {
    const slots = Measurements.getSlots({
        ...timeRange,
        resolution: TimeResolution.month
    });
    const endDate = moment(timeRange.end);
    const yearSlots = Measurements.getSlots({
        start: moment(timeRange.start).startOf("year").toISOString(),
        end: endDate.clone().startOf('year').isSame(endDate) ? endDate.clone().toISOString()
            : endDate.clone().add(1, 'year').startOf("year").toISOString(),
        resolution: TimeResolution.year
    });
    const template = cloneDeep(temp);
    return { slots, yearSlots, dynamicColumns: getDynamicColumns(template, slots, yearSlots), template };
}

function getMeasurements(timeRange: TimeRange, sourceProvider: SourcesProvider<any>, addPower: boolean): Observable<MeasurementsResult[]> {
    return sendQuery("com.flowmaps.api.measurements.GetMeasurementsByConnection", <GetMeasurementsByConnection>{
        resolution: TimeResolution.month,
        timeRange: AppContext.timeRangeToQuery(timeRange),
        sources: sourceProvider.sourceSelectionAfterCleanup(),
        unrounded: true,
        addPower: addPower,
    }, {caching: false, showSpinner: true});
}

function getSubmeterMeasurements(timeRange: TimeRange, sourceProvider: SourcesProvider<any>, entityIds?: string[]): Observable<MeasurementsResult[]> {
    if (entityIds && entityIds.length === 0) {
        return of([]);
    }
    const sourceIds = entityIds ?? getSubmeterMeasurementLocationIds(sourceProvider);

    return sendQuery("com.flowmaps.api.measurements.GetMeasurementsByMeter", <GetMeasurementsByMeter>{
        locationIds: sourceIds,
        resolution: TimeResolution.month,
        timeRange: AppContext.timeRangeToQuery(timeRange),
        topLevelOnly: false,
        unrounded: true,
        addPower: true
    }, {caching: false, showSpinner: true});
}

export function getSubmeterMeasurementLocationIds(sourceProvider: SourcesProvider<any>): string[] {
    return [...new Set(sourceProvider.getTreeViewSelection(sourceProvider.sourceSelectionAfterCleanup()).flatMap(s => {
        const location = s.info.type === "location" ? [s] : [];
        const subLocations = s.subItems?.filter(i => i.info.type === "location") ?? [];
        return [...location, ...subLocations];
    }).map(item => item.info.id).filter(Boolean))];
}


function aggregate(measurements: { [P in DataType]?: AggregatedDataPoint[] }) {
    let data = lodash.cloneDeep(measurements || {});
    for (const [targetType, sources] of dataTypesMapped) {
        const allPoints: AggregatedDataPoint[] = sources.flatMap(dt => data[dt as DataType] || []);
        if (allPoints.length > 0) {
            const grouped = lodash.groupBy(allPoints, dp => dp.timeRange.start);
            data[targetType as DataType] = Object.entries(grouped).map(([_, points]) => ({
                timeRange: points[0].timeRange,
                value: lodash.sumBy(points, p => p.value)
            }));
            sources.forEach(dt => {
                if (dt !== targetType) delete data[dt as DataType];
            });
        }
    }
    return data;
}

export function getConnectionData(timeRange: TimeRange, data: MeasurementsResult[], entities: Entity[], meterViews: MeterViewEntity[],
                                  sourceProvider: SourcesProvider<any>, slots: Slot[], yearSlots: Slot[], dynamicColumns: DynamicColumn[], aggregated: boolean = true) {
    const includeChargePointTotal = !aggregated;
    const allFilteredConnections = sourceProvider.getAllSourcesByType(EntityType.connection)
        .filter(s => (includeChargePointTotal || s.connectionType !== ConnectionType.ChargePoint)
            && !s.source.connection.meters.every(m => m.info.type === MeterType.INTERMEDIATE))
        .map(s => s.source.connection);
    const connectionIdsWithoutData = allFilteredConnections.map(c => c.connectionId);
    const connectionsWithData = data.flatMap(m => {
        const entity = entities.find(c => c.connection.connectionId === m.entityId
            && (includeChargePointTotal || c.connection.info.connectionType != ConnectionType.ChargePoint)
            && !c.connection.meters.every(m => m.info.type === MeterType.INTERMEDIATE));
        if (!entity) return [];
        removeItem(connectionIdsWithoutData, entity.connection.connectionId);
        let measurements = aggregated ? aggregate(m.measurements) :lodash.cloneDeep(m.measurements || {});
        return Object.entries(measurements)
            .filter(e => getAllowedMeasurementTypes(false, !aggregated).includes(e[0] as DataType))
            .filter(e => entity.connection.info.connectionType !== ConnectionType.ChargePoint
                || e[0] === DataType.chargePointConsumption)
            .map(e => {
                const data = {
                    organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
                    location: RefdataUtils.locationInfoFormatter(entity.location.info),
                    buildingCode: entity.location.info.buildingCode,
                    ean: entity.connection.info.code,
                    surveyor: entity.connection.info.surveyor?.name || "",
                    marketSegment: entity.connection.info.marketSegment,
                    connectionType: e[0] === DataType.coolingConsumption ? ConnectionType.Cooling : entity.connection.info.connectionType,
                    measurementType: aggregated ? AppContext.entityPerformanceMeasurementName(e[0] as DataType) : AppContext.measurementName(e[0] as DataType),
                    type: e[0] as DataType,
                    measurementUnit: DashboardContext.getMeasurementUnit(e[0] as DataType, false),
                    completeness: AppContext.computeCompleteness(meterViews.filter(
                        v => v.meterView.connectionId === entity.connection.connectionId), timeRange, [])
                };
                const slotsWithData = Measurements.appendAggregatedDataToSlots(e[1], slots)
                    .concat(Measurements.appendAggregatedDataToSlots(e[1], yearSlots));
                dynamicColumns.forEach(d => {
                    const slot = slotsWithData.find(s => s.label === d.fieldName);
                    if (slot) {
                        data[d.fieldName] = slot.values.length > 0 ? lodash.sum(slot.values) : null;
                    }
                });
                return data;
            });
    });
    const connectionsWithoutData = connectionIdsWithoutData.map(c => {
        const entity = entities.find(con => con.connection.connectionId === c);
        if (entity?.connection.info.connectionType === ConnectionType.ChargePoint) return null;
        return entity ? <any>{
            organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
            location: RefdataUtils.locationInfoFormatter(entity.location.info),
            buildingCode: entity.location.info.buildingCode,
            ean: entity.connection.info.code,
            surveyor: entity.connection.info.surveyor?.name || "",
            marketSegment: entity.connection.info.marketSegment,
            connectionType: entity.connection.info.connectionType
        } : null;
    });
    return sortRecords(connectionsWithData).concat(connectionsWithoutData);
}

export function getSubmeterData(timeRange: TimeRange, data: MeasurementsResult[], entities: Entity[], meterViews: MeterViewEntity[],
                         sourceProvider: SourcesProvider<any>, slots: Slot[], yearSlots: Slot[], dynamicColumns: DynamicColumn[], meterIds?: string[]) {
    const allMeters = selectIntermediateMeters(
        sourceProvider.getAllSourcesByType(EntityType.meter).map(s => s.source.meter), meterIds);
    const allowedMeterIds = new Set(allMeters.map(meter => meter.meterId));
    const metersIdsWithoutData = allMeters.map(m => m.meterId);
    const selectedChargePointConnections = new Set(entities
        .filter(entity => allowedMeterIds.has(entity.meter?.meterId)
            && entity.connection?.info.connectionType === ConnectionType.ChargePoint)
        .map(entity => entity.connection.connectionId));

    const toRows = (entity: Entity, measurementsResult: MeasurementsResult, allowedTypes: DataType[]) => {
        const measurements = aggregate(measurementsResult.measurements);
        return Object.entries(measurements)
            .filter(entry => allowedTypes.includes(entry[0] as DataType))
            .map(entry => {
                const row = {
                    ...buildSubmeterRow(entity, timeRange, meterViews),
                    measurementType: AppContext.entityPerformanceMeasurementName(entry[0] as DataType),
                    type: entry[0] as DataType,
                    measurementUnit: DashboardContext.getMeasurementUnit(entry[0] as DataType, false)
                };
                const slotsWithData = Measurements.appendAggregatedDataToSlots(entry[1], slots)
                    .concat(Measurements.appendAggregatedDataToSlots(entry[1], yearSlots));
                dynamicColumns.forEach(column => {
                    const slot = slotsWithData.find(s => s.label === column.fieldName);
                    if (slot) row[column.fieldName] = slot.values.length > 0 ? lodash.sum(slot.values) : null;
                });
                return row;
            });
    };

    const individualMetersWithData = data.flatMap(measurementsResult => {
        const entity = entities.find(candidate => candidate.meter?.meterId === measurementsResult.entityId);
        if (entity && allowedMeterIds.has(entity.meter.meterId)) {
            removeItem(metersIdsWithoutData, entity.meter.meterId);
            const allowedTypes = getAllowedMeasurementTypes(true, true);
            if (effectiveMeterType(entity.meter) === MeterType.GROSS_PRODUCTION) {
                allowedTypes.push(DataType.electricityGrossProduction);
            }
            return toRows(entity, measurementsResult, allowedTypes);
        }
        return [];
    });

    const chargePointAggregateData = data.flatMap(measurementsResult => {
        const entity = entities.find(candidate =>
            (candidate.meter?.meterId === measurementsResult.entityId
                || candidate.connection?.connectionId === measurementsResult.entityId)
            && (candidate.meter?.details?.type || candidate.meter?.info.type) === MeterType.PRIMARY
            && candidate.connection?.info.connectionType === ConnectionType.ChargePoint);
        if (!entity || (meterIds !== undefined
            && !selectedChargePointConnections.has(entity.connection.connectionId))) return [];
        return toRows(entity, measurementsResult, [DataType.chargePointConsumption]);
    });

    const metersWithoutData = metersIdsWithoutData.map(c => {
        const entity = entities.find(con => con.meter?.meterId === c);
        return entity ? <any>{
            ...buildSubmeterRow(entity, timeRange, meterViews),
            measurementType: null,
            label: entity.meter?.details?.label ?? entity.meter?.info.label ?? null,
            meterType: entity.meter?.details?.type ?? entity.meter?.info.type ?? null,
            completeness: 0,
            ...Object.fromEntries(dynamicColumns.map(d => [d.fieldName, null]))
        } : null;
    });
    const chargePointDetails = sortRecords(individualMetersWithData.filter(row =>
        row.connectionType === ConnectionType.ChargePoint))
        .concat(metersWithoutData.filter(row => row?.connectionType === ConnectionType.ChargePoint));
    const overviewData = sortRecords(individualMetersWithData.filter(row =>
        row.connectionType !== ConnectionType.ChargePoint).concat(chargePointAggregateData))
        .concat(metersWithoutData.filter(row => row?.connectionType !== ConnectionType.ChargePoint));
    return {overviewData, chargePointData: chargePointDetails};
}

export function selectIntermediateMeters(meters: Meter[], meterIds?: string[]): Meter[] {
    const allowedMeterIds = meterIds === undefined ? null : new Set(meterIds);
    return meters
        .filter(meter => [MeterType.INTERMEDIATE, MeterType.GROSS_PRODUCTION].includes(effectiveMeterType(meter)))
        .filter(meter => !isCalculatedResidual(meter) && isMeterVisible(meter))
        .filter(meter => !allowedMeterIds || allowedMeterIds.has(meter.meterId));
}

function getMeterNumbers(entity: Entity): string {
    const sourceMeterNumbers = (entity.meter?.info?.meterNumbers || []).filter(Boolean);
    return sourceMeterNumbers.length ? sourceMeterNumbers.join(', ') : entity.meter?.info?.accessPointId || '';
}

export function buildSubmeterRow(entity: Entity, timeRange: TimeRange, meterViews: MeterViewEntity[]) {
    return {
        organisation: RefdataUtils.organisationInfoFormatter(entity.organisation.info),
        location: RefdataUtils.locationInfoFormatter(entity.location.info),
        buildingCode: entity.location.info.buildingCode,
        ean: entity.connection.info.code,
        source: entity.meter.info.surveyor?.name || entity.connection.info.surveyor?.name || "",
        marketSegment: entity.connection.info.marketSegment,
        connectionType: meterEnergyType(entity.meter, entity.connection),
        label: (entity.meter.details?.label || entity.meter.info.label) ?? "",
        meterType: entity.meter.details?.type || entity.meter.info.type || "",
        meterNumbers: getMeterNumbers(entity),
        tenant: RefdataUtils.getMeterMetadataValue(entity.meter, 'tenant'),
        floor: RefdataUtils.getMeterMetadataValue(entity.meter, 'floor'),
        tower: RefdataUtils.getMeterMetadataValue(entity.meter, 'tower'),
        wing: RefdataUtils.getMeterMetadataValue(entity.meter, 'wing'),
        area: entity.meter.details?.area ?? null,
        measurementUnit: "",
        completeness: AppContext.computeCompleteness(meterViews.filter(
            v => v.meterView.connectionId === entity.connection.connectionId), timeRange, [])
    };
}

function sortRecords(records: any[]) {
    return lodash.sortBy(records.filter(Boolean),
        [
            item => item.organisation?.toLowerCase(),
            item => item.location?.toLowerCase(),
            item => indexOfDataType(item.type),
            item => item.ean,
        ]);
}

function applySubmeterHeaders(sheet: XLSX.WorkSheet, columns: SubmeterExportColumn[]) {
    Object.keys(sheet).filter(key => /^[A-Z]+[0-9]+$/.test(key)).forEach(key => delete sheet[key]);
    columns.forEach((column, index) => {
        sheet[XLSX.utils.encode_cell({r: 0, c: index})] = {
            v: TranslateDirective.getTranslation(column.header, true), t: 's'
        };
    });
    sheet['!ref'] = `A1:${XLSX.utils.encode_col(Math.max(0, columns.length - 1))}1`;
}

interface SubmeterExportColumn {
    key: string;
    header: string;
    optional?: boolean;
    numberFormat?: string;
}

const SUBMETER_EXPORT_COLUMNS: SubmeterExportColumn[] = [
    {key: 'organisation', header: 'Organisation'},
    {key: 'location', header: 'Location'},
    {key: 'buildingCode', header: 'Building code'},
    {key: 'ean', header: 'EAN'},
    {key: 'marketSegment', header: 'Market segment'},
    {key: 'meterNumbers', header: 'Source meter number'},
    {key: 'label', header: 'Meter name'},
    {key: 'meterType', header: 'Meter type'},
    {key: 'tenant', header: 'Tenant', optional: true},
    {key: 'floor', header: 'Floor', optional: true},
    {key: 'tower', header: 'Tower', optional: true},
    {key: 'wing', header: 'Wing', optional: true},
    {key: 'area', header: 'Area', optional: true, numberFormat: '#,##0'},
    {key: 'measurementType', header: 'Measurement type'},
    {key: 'measurementUnit', header: 'Measurement unit'},
    {key: 'completeness', header: 'Completeness', numberFormat: '0%'}
];

export function getSubmeterExportColumnKeys(rows: any[]): string[] {
    return getSubmeterExportColumns(rows).map(column => column.key);
}

function getSubmeterExportColumns(rows: any[]): SubmeterExportColumn[] {
    return SUBMETER_EXPORT_COLUMNS.filter(column => !column.optional
        || rows.some(row => row?.[column.key] !== null && row?.[column.key] !== undefined
            && String(row[column.key]).trim() !== ''));
}

function configureSubmeterTemplate(template: WorkBookTemplate, columns: SubmeterExportColumn[]): void {
    template.sheets.forEach(sheet => {
        const key = sheet.dataKey || 'data';
        sheet.template[key] = Object.fromEntries(columns.map((column, index) => {
            const field = `${XLSX.utils.encode_col(index)}$`;
            return [column.key, column.numberFormat
                ? new QuantityField(field, 0, column.numberFormat) : new RequiredField(field)];
        }));
    });
}

function applyBulkHeaders(sheet: XLSX.WorkSheet) {
    [
        'Organisation', 'Location', 'Building code', 'EAN', 'Surveyor', 'Market segment',
        'Connection type', 'Measurement type', 'Measurement unit', 'Completeness'
    ].forEach((header, index) => {
        sheet[XLSX.utils.encode_cell({r: 0, c: index})] = {
            v: TranslateDirective.getTranslation(header, true), t: 's'
        };
    });
}

function addDynamicColumns(sheet: XLSX.WorkSheet, template: SheetTemplate, dynamicColumns: DynamicColumn[], rowCount: number) {
    const key = template.dataKey || "data";
    dynamicColumns.forEach(d => template.template[key][d.fieldName] = new DynamicField(d.columnIndex, d.header, '#,##0'));
    template.template[key] = new ArrayTemplate(template.template[key], [2, rowCount]);
    Object.values(template.template[key].pattern).filter(e => e["headerName"])
        .forEach((e: DynamicField) => sheet[e.field.replace("$", "1")] = <any>{v: e.headerName, t: "s"});
}

function getAllowedMeasurementTypes (addIntermediateData: boolean, addPower: boolean) {
    let measurements = [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak,
        DataType.electricityFeedIn, DataType.electricityFeedInOffPeak, DataType.electricityGrossProduction, DataType.gasConsumption, DataType.waterConsumption,
        DataType.heatConsumption, DataType.coolingConsumption, DataType.chargePointConsumption];
    if (addIntermediateData) {
        measurements = measurements.concat(intermediaryDataTypes).filter(d => d != DataType.electricityGrossProduction)
    }
    if (addPower) measurements = measurements.concat(DataType.electricityPower, DataType.electricityFeedInPower, DataType.chargePointPower)
    return measurements
}

const intermediaryDataTypes = [DataType.electricityIntermediateConsumption, DataType.waterIntermediateConsumption, DataType.chargePointIntermediateConsumption]

function getDynamicColumns(template: WorkBookTemplate, monthSlots: Slot[], yearSlots: Slot[]) {
    const highestColumnField = (Object.values(template.sheets[0].template.data) as RequiredField[]).reduce(
        (a, b) =>
            excelColToNum(a.field.replace(/[^A-Z]/g, '')) > excelColToNum(b.field.replace(/[^A-Z]/g, ''))
                ? a : b);
    const columns: DynamicColumn[] = [];
    let currentHighestColumnName = highestColumnField.field.replace(/[^A-Z]/g, '');
    monthSlots.forEach(s => {
        currentHighestColumnName = getNextExcelColumnName(currentHighestColumnName);
        columns.push({
            fieldName: s.label,
            columnIndex: currentHighestColumnName + "$",
            header: s.label,
            slot: s
        });
    });
    yearSlots.forEach(s => {
        currentHighestColumnName = getNextExcelColumnName(currentHighestColumnName);
        columns.push({
            fieldName: s.label,
            columnIndex: currentHighestColumnName + "$",
            header: `${TranslateDirective.getTranslation('total', true)} ${s.label}`,
            slot: s
        });
    })
    return columns;
}

function excelColToNum(text: string): number {
    let num = 0;
    text = text.replace(/[^A-Z]/gi, '').toUpperCase();
    for (let i = 0; i < text.length; i++) {
        num = num * 26 + (text.charCodeAt(i) - 64);
    }
    return num;
}

function getNextExcelColumnName(columnName: string) {
    let result = columnName.split('');
    for (let i = result.length - 1; i >= 0; i--) {
        if (result[i] === 'Z') {
            result[i] = 'A';
            if (i === 0) {
                result.unshift('A');
                break;
            }
        } else {
            result[i] = String.fromCharCode(result[i].charCodeAt(0) + 1);
            break;
        }
    }
    return result.join('');
}

const dataTypesMapped = new Map<string, DataType[]>([
    [DataType.electricityConsumption, [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak]],
    [DataType.electricityFeedIn, [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak]]
]);


const bulkDataDownloadTemplate: WorkBookTemplate = {
    sheets: [
        {
            name: 'Data',
            template: {
                data: {
                    organisation: new RequiredField('A$'),
                    location: new RequiredField('B$'),
                    buildingCode: new RequiredField('C$'),
                    ean: new RequiredField('D$'),
                    surveyor: new RequiredField('E$'),
                    marketSegment: new RequiredField('F$'),
                    connectionType: new RequiredField('G$'),
                    measurementType: new RequiredField('H$'),
                    measurementUnit: new RequiredField("I$"),
                    completeness: new QuantityField("J$", 0, "0%"),
                    // Data columns are added when creating data
                }
            }
        }
    ]
};

const submetersDataDownloadTemplate: WorkBookTemplate = {
    sheets: [
        {
            name: 'Data',
            template: {
                data: {
                    organisation: new RequiredField('A$'),
                    location: new RequiredField('B$'),
                    buildingCode: new RequiredField('C$'),
                    ean: new RequiredField('D$'),
                    marketSegment: new RequiredField('E$'),
                    meterNumbers: new RequiredField('F$'),
                    label: new RequiredField('G$'),
                    meterType: new RequiredField('H$'),
                    tenant: new RequiredField('I$'),
                    floor: new RequiredField('J$'),
                    tower: new RequiredField('K$'),
                    wing: new RequiredField('L$'),
                    area: new QuantityField('M$', 0, '#,##0'),
                    measurementType: new RequiredField('N$'),
                    measurementUnit: new RequiredField('O$'),
                    completeness: new QuantityField('P$', 0, '0%'),
                }
            },
            dataKey: "data"
        },
        {
            name: 'ChargePoints',
            template: {
                chargePoints: {
                    organisation: new RequiredField('A$'),
                    location: new RequiredField('B$'),
                    buildingCode: new RequiredField('C$'),
                    ean: new RequiredField('D$'),
                    marketSegment: new RequiredField('E$'),
                    meterNumbers: new RequiredField('F$'),
                    label: new RequiredField('G$'),
                    meterType: new RequiredField('H$'),
                    tenant: new RequiredField('I$'),
                    floor: new RequiredField('J$'),
                    tower: new RequiredField('K$'),
                    wing: new RequiredField('L$'),
                    area: new QuantityField('M$', 0, '#,##0'),
                    measurementType: new RequiredField('N$'),
                    measurementUnit: new RequiredField('O$'),
                    completeness: new QuantityField('P$', 0, '0%'),
                }
            },
            dataKey: "chargePoints"
        }
    ]
};

const sortedDataTypes: DataType[] = [
    // Electricity
    DataType.electricityConsumption,
    DataType.electricityConsumptionOffPeak,
    DataType.electricityPower,
    DataType.electricityFeedIn,
    DataType.electricityFeedInOffPeak,
    DataType.electricityFeedInPower,
    DataType.electricityGrossProduction,
    DataType.chargePointConsumption,
    DataType.electricityIntermediateConsumption,
    // Gas
    DataType.gasConsumption,
    // Heat and cooling
    DataType.heatConsumption,
    DataType.coolingConsumption,
    // water
    DataType.waterConsumption,
    DataType.waterIntermediateConsumption,
    // chargePoints
    DataType.chargePointPower,
    DataType.chargePointIntermediateConsumption
];

export const indexOfDataType = (measurementType?: DataType | null): number => {
    if (!measurementType) {
        return Number.MAX_SAFE_INTEGER;
    }
    const index = sortedDataTypes.indexOf(measurementType);
    return index === -1 ? Number.MAX_SAFE_INTEGER : (index + 1) * 100;
}

interface DynamicColumn {
    fieldName: string;
    columnIndex: string;
    header: string;
    slot: Slot;
}

interface BulkDownloadInfo {
    slots: Slot[];
    yearSlots: Slot[];
    dynamicColumns: DynamicColumn[];
    template: WorkBookTemplate;
}
